/**
 * OperatorProfile — "Your profile". The operator's standing self-context,
 * entered once and pulled into every read's "your side" automatically.
 *
 *   - Date of birth  → builds your own behavioral read (the real "your side"
 *                      in two-sided matchups)
 *   - Company / role → injected as background so reads fit your world
 *   - Background      → a few lines on how you operate
 *   - Default goals   → pre-fills the goal/context fields on each surface
 */

import { useState, useEffect } from "react";
import { useSession } from "../lib/useSession.js";
import { Wordmark, SectionHeader, Callout } from "../components/index.js";
import { parseJsonResponse } from "../lib/api-fetch.js";

export default function OperatorProfilePage() {
  const { session, loading } = useSession();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dobMonth, setDobMonth] = useState("");
  const [dobDay, setDobDay] = useState("");
  const [dobYear, setDobYear] = useState("");
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [background, setBackground] = useState("");
  const [goals, setGoals] = useState("");

  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!session) return;
    fetch("/.netlify/functions/operator-profile", {
      headers: { authorization: `Bearer ${session.access_token}` },
    })
      .then(parseJsonResponse)
      .then((d) => {
        const c = d.operator_context || {};
        setCompany(c.company || "");
        setRole(c.role || "");
        setBackground(c.background || "");
        setGoals(c.goals || "");
        const sp = d.self_profile;
        if (sp) {
          const parts = (sp.display_name || "").trim().split(/\s+/);
          setFirstName(parts[0] || "");
          setLastName(parts.slice(1).join(" ") || "");
          if (sp.dob_year) setDobYear(String(sp.dob_year));
          if (sp.dob_month) setDobMonth(String(sp.dob_month));
          if (sp.dob_day) setDobDay(String(sp.dob_day));
        } else if (d.display_name) {
          const parts = d.display_name.trim().split(/\s+/);
          setFirstName(parts[0] || "");
          setLastName(parts.slice(1).join(" ") || "");
        }
      })
      .catch((e) => setError(e.message));
  }, [session]);

  const save = async () => {
    setError(null);
    setSaving(true);
    const dobFilled = dobMonth && dobDay && dobYear;
    try {
      const res = await fetch("/.netlify/functions/operator-profile", {
        method: "PUT",
        headers: { "content-type": "application/json", authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          first_name: firstName || null,
          last_name: lastName || null,
          dob: dobFilled ? { year: Number(dobYear), month: Number(dobMonth), day: Number(dobDay) } : null,
          company: company || null,
          role: role || null,
          background: background || null,
          goals: goals || null,
        }),
      });
      const d = await parseJsonResponse(res);
      if (!res.ok) { setError(d.error || `Save failed (HTTP ${res.status})`); setSaving(false); return; }
      setSavedAt(Date.now());
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p style={{ padding: 24 }}>Loading…</p>;
  if (!session) return <p style={{ padding: 24 }}>Sign in to edit your profile.</p>;

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "32px 24px 60px" }}>
      <div style={{ marginBottom: 24 }}><Wordmark scale={1.0} /></div>
      <SectionHeader title="Your profile" />

      <Callout.Tip>
        Set this once. Your reads pull it in automatically — your date of birth makes the
        relationship and negotiation surfaces a true two-sided read, and your company, background,
        and goals tailor every read to your world so you're not retyping context each time.
      </Callout.Tip>

      <div style={{ display: "grid", gap: 16, marginTop: 20 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="First name">
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Russ" style={inputStyle} />
          </Field>
          <Field label="Last name (optional)">
            <input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Cersosimo" style={inputStyle} />
          </Field>
        </div>

        <Field label="Your date of birth — builds your own behavioral read">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, maxWidth: 360 }}>
            <input type="number" min="1" max="12" placeholder="MM" value={dobMonth} onChange={(e) => setDobMonth(e.target.value)} style={inputStyle} />
            <input type="number" min="1" max="31" placeholder="DD" value={dobDay} onChange={(e) => setDobDay(e.target.value)} style={inputStyle} />
            <input type="number" min="1900" max="2100" placeholder="YYYY" value={dobYear} onChange={(e) => setDobYear(e.target.value)} style={inputStyle} />
          </div>
        </Field>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Company">
            <input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Marabot AI for Realtors" style={inputStyle} />
          </Field>
          <Field label="Your role">
            <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="CEO" style={inputStyle} />
          </Field>
        </div>

        <Field label="Background — a few lines on how you operate">
          <textarea value={background} onChange={(e) => setBackground(e.target.value)} rows={4}
            placeholder="20 years in real estate tech; I sell AI tooling to brokerages; I move fast and lead with ROI…" style={inputStyle} />
        </Field>

        <Field label="Default goals — what you're usually trying to do">
          <textarea value={goals} onChange={(e) => setGoals(e.target.value)} rows={3}
            placeholder="Land annual contracts with regional brokerages; expand existing accounts…" style={inputStyle} />
        </Field>

        {error && <ErrorBox error={error} />}

        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <button onClick={save} disabled={saving} style={btnStyle}>
            {saving ? "Saving…" : "Save your profile"}
          </button>
          {savedAt && !saving && (
            <span style={{ fontFamily: "var(--ppr-typography-font-family-ui)", fontSize: 13, color: "var(--ppr-color-text-secondary)" }}>
              Saved.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  padding: "12px 14px",
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: "var(--ppr-typography-font-size-base)",
  border: "1px solid var(--ppr-color-border-default)",
  borderRadius: "var(--ppr-radius-sm)",
  background: "var(--ppr-color-background-surface)",
  color: "var(--ppr-color-text-primary)",
  width: "100%",
  outline: "none",
  resize: "vertical",
};

const btnStyle = {
  padding: "12px 24px",
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: "var(--ppr-typography-font-size-md)",
  letterSpacing: "0.02em",
  border: "1px solid var(--ppr-color-accent-navy)",
  borderRadius: "var(--ppr-radius-sm)",
  background: "var(--ppr-color-accent-navy)",
  color: "var(--ppr-color-text-inverse)",
  cursor: "pointer",
};

function Field({ label, children }) {
  return (
    <div>
      <label style={{
        display: "block",
        fontFamily: "var(--ppr-typography-font-family-ui)",
        fontSize: "12px",
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        color: "var(--ppr-color-text-secondary)",
        marginBottom: 6,
      }}>{label}</label>
      {children}
    </div>
  );
}

function ErrorBox({ error }) {
  return (
    <div style={{
      padding: "12px 14px",
      border: "1px solid var(--ppr-color-accent-coral)",
      borderRadius: "var(--ppr-radius-sm)",
      background: "var(--ppr-color-accent-coral-very-soft)",
      color: "var(--ppr-color-accent-coral-dark)",
    }}>
      {error}
    </div>
  );
}
